import { toast } from 'sonner'

import {
  useGenerateInvoicePdfMutation,
  useGenerateQuotePdfMutation,
  useInvoicePdfSignedUrlMutation,
  useInvoicePdfVersionsQuery,
  useQuotePdfSignedUrlMutation,
  useQuotePdfVersionsQuery,
} from '@/hooks'
import { downloadFile } from '@/lib/download-file'

export type PdfActionProps = (
  | { entityType: 'invoice'; entityId: string; entityUpdatedAt: string }
  | { entityType: 'quote'; entityId: string; entityUpdatedAt: string }
) & {
  /**
   * Whether the organization currently meets the invoicing-readiness gate for this entity
   * type. Blocks only the "Generate PDF" action — an already-generated PDF stays
   * downloadable regardless, since it reflects org details that were valid at the time.
   */
  canGenerate?: boolean
}

export type PdfActionState =
  | { kind: 'loading'; label: string }
  | { kind: 'blocked'; label: string; blockedReason: string }
  | { kind: 'generate'; label: string; onSelect: () => void }
  | { kind: 'download'; label: string; onSelect: () => void; isDisabled: boolean }

export function usePdfAction({
  entityType,
  entityId,
  entityUpdatedAt,
  canGenerate = true,
}: PdfActionProps): PdfActionState {
  /* Both hook sets called unconditionally (React rules). Inactive set uses
     empty ID with enabled: false. */
  const invoiceVersionsQuery = useInvoicePdfVersionsQuery(
    entityType === 'invoice' ? entityId : '',
  )
  const quoteVersionsQuery = useQuotePdfVersionsQuery(
    entityType === 'quote' ? entityId : '',
  )
  const invoiceGenerateMutation = useGenerateInvoicePdfMutation(
    entityType === 'invoice' ? entityId : '',
  )
  const quoteGenerateMutation = useGenerateQuotePdfMutation(
    entityType === 'quote' ? entityId : '',
  )
  const invoiceSignedUrlMutation = useInvoicePdfSignedUrlMutation()
  const quoteSignedUrlMutation = useQuotePdfSignedUrlMutation()

  const versionsQuery =
    entityType === 'invoice' ? invoiceVersionsQuery : quoteVersionsQuery
  const generateMutation =
    entityType === 'invoice' ? invoiceGenerateMutation : quoteGenerateMutation
  const signedUrlMutation =
    entityType === 'invoice' ? invoiceSignedUrlMutation : quoteSignedUrlMutation

  const {
    data: versionsResult,
    isLoading: isVersionsLoading,
    isFetching: isVersionsFetching,
  } = versionsQuery
  const versions = versionsResult?.data ?? []
  const latestVersion = versions.find((version) => !version.isArchived)
  const isStale =
    !latestVersion || new Date(entityUpdatedAt) > new Date(latestVersion.generatedAt)

  function handleDownload() {
    const onSuccess = (result: { data: { url: string; filename: string } | null }) => {
      if (!result.data) return
      downloadFile(result.data.url, result.data.filename).catch(
        (caughtError: unknown) => {
          const message =
            caughtError instanceof Error ? caughtError.message : 'Could not download PDF'
          toast.error('Failed to download PDF', { description: message })
        },
      )
    }
    const onError = (caughtError: unknown) => {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Could not open PDF'
      const isExpired =
        message.toLowerCase().includes('expired') ||
        message.toLowerCase().includes('no longer')
      toast.error(isExpired ? 'PDF expired' : 'Failed to open PDF', {
        description: isExpired
          ? 'This PDF is no longer available. Please generate a new one.'
          : message,
      })
    }

    if (entityType === 'invoice') {
      invoiceSignedUrlMutation.mutate({ invoiceId: entityId }, { onSuccess, onError })
    } else {
      quoteSignedUrlMutation.mutate({ quoteId: entityId }, { onSuccess, onError })
    }
  }

  function handleGenerate() {
    generateMutation.mutate(undefined, {
      onSuccess: handleDownload,
      onError: (caughtError) => {
        const message =
          caughtError instanceof Error ? caughtError.message : 'PDF generation failed'
        toast.error('Failed to generate PDF', { description: message })
      },
    })
  }

  const blockedReason = `Complete your organization's ${
    entityType === 'invoice' ? 'business, tax, and payment' : 'business and tax'
  } details in Settings before generating a PDF.`

  if (isVersionsLoading || isVersionsFetching || generateMutation.isPending) {
    return {
      kind: 'loading',
      label: generateMutation.isPending ? 'Generating…' : 'PDF',
    }
  }
  if (isStale) {
    if (!canGenerate) return { kind: 'blocked', label: 'Generate PDF', blockedReason }
    return { kind: 'generate', label: 'Generate PDF', onSelect: handleGenerate }
  }
  return {
    kind: 'download',
    label: 'Download PDF',
    onSelect: handleDownload,
    isDisabled: signedUrlMutation.isPending,
  }
}
